/**
 * Utility functions for exporting and importing budgets as CSV
 */

const escapeCsvValue = (value) => {
  const str = String(value ?? '');
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

/**
 * Convert budgets to CSV format.
 */
export const convertBudgetsToCSV = (budgets, categories = [], accounts = []) => {
  const categoryMap = new Map();
  categories.forEach(cat => categoryMap.set(cat.id, cat.name));
  const accountMap = new Map();
  accounts.forEach(acc => accountMap.set(acc.id, acc.name));

  const headers = [
    'Budget ID', 'Name', 'Category', 'Category ID', 'Account', 'Account ID',
    'Amount', 'Type', 'Start Date', 'End Date',
    'Recurring', 'Period', 'Prorated', 'Active', 'Notes', 'Amount History'
  ];

  const rows = budgets.map(b => [
    escapeCsvValue(b.id || ''),
    escapeCsvValue(b.name || ''),
    escapeCsvValue(categoryMap.get(b.categoryId) || ''),
    escapeCsvValue(b.categoryId || ''),
    escapeCsvValue(accountMap.get(b.accountId) || 'All Accounts'),
    escapeCsvValue(b.accountId || ''),
    b.amount ?? '',
    escapeCsvValue(b.type || 'Expense'),
    escapeCsvValue(b.startDate || ''),
    escapeCsvValue(b.endDate || ''),
    b.isRecurring ? 'Yes' : 'No',
    escapeCsvValue(b.recurringPeriod || ''),
    b.isProrated ? 'Yes' : 'No',
    b.isActive !== false ? 'Yes' : 'No',
    escapeCsvValue(b.notes || ''),
    escapeCsvValue(JSON.stringify(b.amountHistory || []))
  ].join(','));

  return [headers.join(','), ...rows].join('\n');
};

const downloadFile = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: `${mimeType};charset=utf-8;` });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Export budgets to CSV file
 */
export const exportBudgetsToCSV = (budgets, categories, accounts) => {
  const csvContent = convertBudgetsToCSV(budgets, categories, accounts);
  const filename = `budgets_${new Date().toISOString().split('T')[0]}.csv`;
  downloadFile(csvContent, filename, 'text/csv');
};

const parseCSVLine = (line) => {
  const values = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"' && line[i + 1] === '"') {
        current += '"';
        i++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ',') {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
  }
  values.push(current.trim());
  return values;
};

/**
 * Parse imported budgets from CSV content.
 */
export const parseImportedBudgetsCSV = (csvContent, categories = [], accounts = []) => {
  const lines = csvContent.split(/\r?\n/).filter(line => line.trim());
  if (lines.length < 2) {
    throw new Error('CSV file must have a header row and at least one data row');
  }

  const header = parseCSVLine(lines[0]).map(h => h.toLowerCase().trim());
  const requiredHeaders = ['name', 'category', 'account', 'amount', 'type', 'start date', 'recurring', 'period', 'prorated', 'active'];
  const missingHeaders = requiredHeaders.filter(h => !header.includes(h));
  if (missingHeaders.length > 0) {
    throw new Error(`Missing required column${missingHeaders.length > 1 ? 's' : ''}: ${missingHeaders.map(h => `"${h}"`).join(', ')}. Your CSV must include all required columns.`);
  }

  const col = (name) => header.indexOf(name);
  const categoryIds = new Set(categories.map(c => c.id));
  const categoryNameMap = new Map();
  categories.forEach(c => categoryNameMap.set(c.name.toLowerCase().trim(), c.id));
  const accountIds = new Set(accounts.map(a => a.id));
  const accountNameMap = new Map();
  accounts.forEach(a => accountNameMap.set(a.name.toLowerCase().trim(), a.id));

  const warnings = [];
  const budgets = [];

  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    if (values.length < 2) continue;

    const get = (name) => {
      const idx = col(name);
      return idx >= 0 ? (values[idx] || '') : '';
    };

    const name = get('name');
    const amount = parseFloat(get('amount'));
    if (!name.trim()) {
      warnings.push(`Row ${i + 1}: missing budget name, skipped`);
      continue;
    }
    if (isNaN(amount)) {
      warnings.push(`Row ${i + 1} ("${name}"): invalid or missing amount, skipped`);
      continue;
    }

    // Resolve category
    let categoryId = get('category id');
    if (!categoryId || !categoryIds.has(categoryId)) {
      const catName = get('category');
      if (catName) {
        categoryId = categoryNameMap.get(catName.toLowerCase().trim()) || '';
      } else {
        categoryId = '';
      }
    }
    if (!categoryId) {
      warnings.push(`Row ${i + 1} ("${name}"): category "${get('category') || get('category id')}" not matched to any existing category, skipped`);
      continue;
    }

    // Resolve account
    let accountId = get('account id');
    if (!accountId || !accountIds.has(accountId)) {
      const accName = get('account');
      if (accName && accName.toLowerCase() !== 'all accounts') {
        accountId = accountNameMap.get(accName.toLowerCase().trim()) || '';
      } else {
        accountId = '';
      }
    }
    if (!accountId) {
      warnings.push(`Row ${i + 1} ("${name}"): account "${get('account') || get('account id')}" not matched to any existing account, skipped`);
      continue;
    }

    // Validate type
    const type = get('type');
    if (!type || !['income', 'expense'].includes(type.toLowerCase())) {
      warnings.push(`Row ${i + 1} ("${name}"): invalid or missing type (must be "Income" or "Expense"), skipped`);
      continue;
    }

    // Validate start date
    let startDate = get('start date');
    if (startDate) {
      // Accept ISO timestamps (e.g. 2025-01-01T00:00:00.000Z) — extract date part
      const dateMatch = startDate.match(/^(\d{4}-\d{2}-\d{2})/);
      if (dateMatch) {
        startDate = dateMatch[1];
      } else {
        warnings.push(`Row ${i + 1} ("${name}"): invalid start date "${startDate}" (must be YYYY-MM-DD), skipped`);
        continue;
      }
    } else {
      startDate = new Date().toISOString().split('T')[0];
    }

    // Validate recurring
    const recurringVal = get('recurring');
    if (!recurringVal || !['yes', 'no'].includes(recurringVal.toLowerCase())) {
      warnings.push(`Row ${i + 1} ("${name}"): invalid or missing recurring value (must be "Yes" or "No"), skipped`);
      continue;
    }

    // Validate period
    const period = get('period');
    if (!period || !['weekly', 'fortnightly', 'monthly', 'quarterly', 'yearly'].includes(period.toLowerCase())) {
      warnings.push(`Row ${i + 1} ("${name}"): invalid or missing period (must be weekly, fortnightly, monthly, quarterly, or yearly), skipped`);
      continue;
    }

    // Validate prorated
    const proratedVal = get('prorated');
    if (!proratedVal || !['yes', 'no'].includes(proratedVal.toLowerCase())) {
      warnings.push(`Row ${i + 1} ("${name}"): invalid or missing prorated value (must be "Yes" or "No"), skipped`);
      continue;
    }

    // Validate active
    const activeVal = get('active');
    if (!activeVal || !['yes', 'no'].includes(activeVal.toLowerCase())) {
      warnings.push(`Row ${i + 1} ("${name}"): invalid or missing active value (must be "Yes" or "No"), skipped`);
      continue;
    }

    const yesNo = (val) => val.toLowerCase() === 'yes';

    let amountHistory = [];
    try {
      amountHistory = JSON.parse(get('amount history') || '[]');
      if (!Array.isArray(amountHistory) || amountHistory.some(v => !v ||
        typeof v.effectiveFrom !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(v.effectiveFrom) ||
        typeof v.amount !== 'number' || !Number.isFinite(v.amount) || v.amount < 0)) throw new Error();
    } catch (_) {
      warnings.push(`Row ${i + 1}: invalid amount history, skipped`);
      continue;
    }
    budgets.push({
      amountHistory,
      name: name,
      categoryId,
      accountId,
      amount,
      type: type.charAt(0).toUpperCase() + type.slice(1).toLowerCase(),
      startDate,
      endDate: get('end date') || '',
      isRecurring: yesNo(recurringVal),
      recurringPeriod: period.toLowerCase(),
      isProrated: yesNo(proratedVal),
      isActive: yesNo(activeVal),
      notes: get('notes') || ''
    });
  }

  return { budgets, warnings };
};

/**
 * Generate a CSV template string for budget imports
 */
export const getBudgetCSVTemplate = () => {
  return `Name,Category,Account,Amount,Type,Start Date,End Date,Recurring,Period,Prorated,Active,Notes
Monthly Groceries,Groceries,Main Account,500,Expense,2025-01-01,,Yes,monthly,No,Yes,Weekly grocery budget
Salary,Income,Main Account,5000,Income,2025-01-01,,Yes,monthly,No,Yes,Monthly salary`;
};

